import React from 'react';
import PropTypes from 'prop-types';

const ReportsSummary = ({ summary, isLoading, year }) => {
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No summary data available for {year}</p>
      </div>
    );
  }

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatPercentage = (rate) => {
    return (rate * 100).toFixed(1) + '%';
  };

  const stripRedundantPrefix = (description, code) => {
    if (!description || !code) return description;

    // Find the first space in the description
    const firstSpaceIndex = description.indexOf(' ');
    if (firstSpaceIndex === -1) return description;

    // Get the first word (everything up to the first space)
    const firstWord = description.substring(0, firstSpaceIndex);

    // If the first word matches the code, strip it
    if (firstWord === code) {
      return description.substring(firstSpaceIndex + 1).trim();
    }

    return description;
  };

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Cases
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatNumber(summary.totalCases)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Charges
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatNumber(summary.totalCharges)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Courthouses
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {Object.keys(summary.courthouseStats).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                    />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Judges
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {Object.keys(summary.judgeStats).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top Charges */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Top Charges in {year}
          </h3>

          {/* Mobile Layout - Card-based */}
          <div className="lg:hidden space-y-3">
            {summary.topCharges.map((charge, index) => (
              <div
                key={charge.chargeCode}
                className="bg-gray-50 border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 mb-1">
                      {charge.chargeCode}
                    </div>
                    <div className="text-sm text-gray-600 line-clamp-2">
                      {stripRedundantPrefix(
                        charge.chargeDescription,
                        charge.chargeCode
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <div className="text-lg font-semibold text-gray-900">
                      {formatNumber(charge.totalFiled)}
                    </div>
                    <div className="text-xs text-gray-500">Count</div>
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-gray-900">
                      {formatPercentage(charge.outcomeStats.convictionRate)}
                    </div>
                    <div className="text-xs text-gray-500">Convicted</div>
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-gray-900">
                      {charge.outcomeStats.averageSentenceMonths
                        ? `${charge.outcomeStats.averageSentenceMonths.toFixed(0)}m`
                        : 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500">Avg Sentence</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Layout - Table */}
          <div className="hidden lg:block overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Charge Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Count
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Conviction Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Avg Sentence
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {summary.topCharges.map((charge, index) => (
                  <tr
                    key={charge.chargeCode}
                    className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {charge.chargeCode}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div
                        className="max-w-xs truncate"
                        title={charge.chargeDescription}
                      >
                        {stripRedundantPrefix(
                          charge.chargeDescription,
                          charge.chargeCode
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatNumber(charge.totalFiled)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatPercentage(charge.outcomeStats.convictionRate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {charge.outcomeStats.averageSentenceMonths
                        ? `${charge.outcomeStats.averageSentenceMonths.toFixed(1)} months`
                        : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Courthouse Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Cases by Courthouse
            </h3>
            <div className="space-y-3">
              {Object.entries(summary.courthouseStats)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 10)
                .map(([courthouse, count]) => (
                  <div
                    key={courthouse}
                    className="flex justify-between items-center"
                  >
                    <span className="text-sm text-gray-900 truncate pr-2">
                      {courthouse}
                    </span>
                    <span className="text-sm font-medium text-gray-900 flex-shrink-0">
                      {formatNumber(count)}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Top Judges by Case Count
            </h3>
            <div className="space-y-3">
              {Object.entries(summary.judgeStats)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 10)
                .map(([judge, count]) => (
                  <div
                    key={judge}
                    className="flex justify-between items-center"
                  >
                    <span className="text-sm text-gray-900 truncate pr-2">
                      {judge}
                    </span>
                    <span className="text-sm font-medium text-gray-900 flex-shrink-0">
                      {formatNumber(count)}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ReportsSummary.propTypes = {
  summary: PropTypes.shape({
    totalCases: PropTypes.number,
    totalCharges: PropTypes.number,
    courthouseStats: PropTypes.array,
    judgeStats: PropTypes.array,
    topCharges: PropTypes.arrayOf(
      PropTypes.shape({
        chargeCode: PropTypes.string,
        chargeDescription: PropTypes.string,
        totalFiled: PropTypes.number,
        convictionRate: PropTypes.number
      })
    )
  }),
  isLoading: PropTypes.bool.isRequired,
  year: PropTypes.string.isRequired
};

export default ReportsSummary;
